import { Alert, Box, Button, Skeleton, Snackbar, Stack, Typography } from '@mui/material';
import type {
  PlannerAllocation,
  UtilisationGranularity,
  UtilisationMode,
} from '@timelog/pm-shared';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { usePlannerPerspective, useRpDepartments, useRpResources, useRpRoles } from '@/api';
import { Panels } from '@/components/layouts';
import { MaterialSymbol } from '@/components/MaterialSymbol';
import {
  buildPlannerModel,
  CELL_WIDTH_BY_SCALE,
  FindAllocateDrawer,
  isPlannerMetric,
  MoveHoursPopover,
  PlannerGrid,
  plannerBuckets,
  plannerRange,
  PlannerToolbar,
  todayLineX,
} from '@/components/resourceplanning';
import type { PlannerMetric, PlannerRow, PlannerTab } from '@/components/resourceplanning';
import { todayIso } from '@/lib';
import { usePersistedState } from '@/lib/usePersistedState';

const isTab = (value: unknown): value is PlannerTab => value === 'people' || value === 'demand';
const isScale = (value: unknown): value is Exclude<UtilisationGranularity, 'day'> =>
  value === 'week' || value === 'month' || value === 'quarter';

const TabButton = ({
  label,
  active,
  automationId,
  onClick,
}: {
  label: string;
  active: boolean;
  automationId: string;
  onClick: () => void;
}) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    data-automation-id={automationId}
    aria-selected={active}
    sx={{
      border: 0,
      background: 'transparent',
      cursor: 'pointer',
      p: '8px 2px 11px',
      mr: 2.5,
      borderBottom: `2px solid ${active ? 'var(--color-pink-80)' : 'transparent'}`,
    }}
  >
    <Typography variant="bodyRegular" sx={{ color: active ? 'var(--fg-1)' : 'var(--fg-3)' }}>
      {label}
    </Typography>
  </Box>
);

/**
 * The Resource Planner (M10/M11 UI): the resource owner's tenant-wide
 * utilisation grid — People and Competency-demand views over the planner
 * perspective, with Move-hours and Find & Allocate on top.
 */
export const ResourcePlannerPage = () => {
  const { t } = useTranslation(['pmResources', 'pmCommon']);
  const departmentsQuery = useRpDepartments();
  const resourcesQuery = useRpResources();
  const rolesQuery = useRpRoles();

  // Captured once per mount: a page open across midnight keeps its window.
  const [today] = useState(todayIso);
  const range = useMemo(() => plannerRange(today), [today]);

  const [tab, setTab] = usePersistedState<PlannerTab>('pm-resource-planner-tab', 'people', isTab);
  const [granularity, setGranularity] = usePersistedState<Exclude<UtilisationGranularity, 'day'>>(
    'pm-resource-planner-scale',
    'month',
    isScale,
  );
  const [metric, setMetric] = usePersistedState<PlannerMetric>(
    'pm-resource-planner-metric',
    'pct',
    isPlannerMetric,
  );
  const [mode, setMode] = useState<UtilisationMode>('raw');
  // Group rows are open unless collapsed; resource rows closed unless expanded.
  const [collapsedKeys, setCollapsedKeys] = useState<ReadonlySet<string>>(new Set());
  const [expandedKeys, setExpandedKeys] = useState<ReadonlySet<string>>(new Set());
  const [moveTarget, setMoveTarget] = useState<{
    allocation: PlannerAllocation;
    anchor: HTMLElement;
  } | null>(null);
  const [finderOpen, setFinderOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const plannerQuery = usePlannerPerspective({ from: range.from, to: range.to, granularity, mode });

  const buckets = useMemo(() => plannerBuckets(range, granularity), [range, granularity]);
  const [scrollElement, setScrollElement] = useState<HTMLElement | null>(null);

  const model = useMemo(() => {
    if (!plannerQuery.data || !departmentsQuery.data || !resourcesQuery.data || !rolesQuery.data) {
      return null;
    }
    return buildPlannerModel({
      tab,
      departments: departmentsQuery.data.departments,
      resources: resourcesQuery.data.resources,
      roles: rolesQuery.data.roles,
      planner: plannerQuery.data,
      buckets,
      collapsedKeys,
      expandedKeys,
      t,
    });
  }, [
    plannerQuery.data,
    departmentsQuery.data,
    resourcesQuery.data,
    rolesQuery.data,
    tab,
    buckets,
    collapsedKeys,
    expandedKeys,
    t,
  ]);

  const todayX = useMemo(
    () => todayLineX(today, buckets, granularity, CELL_WIDTH_BY_SCALE[granularity]),
    [today, buckets, granularity],
  );

  const onToggle = (row: PlannerRow) => {
    if (row.kind === 'resource') {
      const next = new Set(expandedKeys);
      if (next.has(row.key)) next.delete(row.key);
      else next.add(row.key);
      setExpandedKeys(next);
      return;
    }
    const next = new Set(collapsedKeys);
    if (next.has(row.key)) next.delete(row.key);
    else next.add(row.key);
    setCollapsedKeys(next);
  };

  const queries = [departmentsQuery, resourcesQuery, rolesQuery, plannerQuery];
  if (queries.some((query) => query.isPending)) {
    return (
      <Box sx={{ p: 3 }} data-automation-id="resource-planner-page">
        <Skeleton height={72} sx={{ mb: 2 }} />
        <Skeleton height={320} />
      </Box>
    );
  }
  const error = queries.find((query) => query.error)?.error;
  if (error || !model) {
    return (
      <Box sx={{ p: 3 }} data-automation-id="resource-planner-page">
        <Alert severity="error">{error?.message ?? t('page.ErrorNotAvailable')}</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}
      data-automation-id="resource-planner-page"
    >
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <Panels autoSaveId="pm-resource-planner-panels">
          <Panels.Main
            contentRef={setScrollElement}
            panelContentProps={{ hasPaddingBottom: false }}
          >
            <Stack
              direction="row"
              sx={{ alignItems: 'center', gap: 1.5, px: 2, pt: 0.5 }}
              data-automation-id="planner-header"
            >
              <MaterialSymbol name="groups" size={22} color="var(--fg-2)" />
              <Typography variant="h3" component="h1">
                {t('page.Title')}
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Button
                variant="contained"
                size="small"
                startIcon={<MaterialSymbol name="person_search" size={16} />}
                onClick={() => setFinderOpen(true)}
                data-automation-id="find-allocate-button"
              >
                {t('page.FindAllocateButton')}
              </Button>
            </Stack>
            <Stack direction="row" sx={{ px: 2 }}>
              <TabButton
                label={t('page.TabPeople')}
                active={tab === 'people'}
                automationId="planner-tab-people"
                onClick={() => setTab('people')}
              />
              <TabButton
                label={t('page.TabCompetencyDemand')}
                active={tab === 'demand'}
                automationId="planner-tab-demand"
                onClick={() => setTab('demand')}
              />
            </Stack>
            <PlannerToolbar
              mode={mode}
              onMode={setMode}
              granularity={granularity}
              onGranularity={(next) => {
                if (isScale(next)) setGranularity(next);
              }}
              metric={metric}
              onMetric={setMetric}
              overCapacityCells={model.overCapacityCells}
            />
            <PlannerGrid
              rows={model.rows}
              buckets={buckets}
              granularity={granularity}
              metric={metric}
              tab={tab}
              todayX={todayX}
              scrollElement={scrollElement}
              onToggle={onToggle}
              onMoveHours={(allocation, anchor) => setMoveTarget({ allocation, anchor })}
            />
          </Panels.Main>
        </Panels>
      </Box>

      <MoveHoursPopover
        allocation={moveTarget?.allocation ?? null}
        roleName={
          rolesQuery.data?.roles.find((role) => role.id === moveTarget?.allocation.roleId)?.name ??
          t('page.FallbackCompetency')
        }
        anchor={moveTarget?.anchor ?? null}
        onClose={() => setMoveTarget(null)}
        onMoved={setToast}
      />
      <FindAllocateDrawer
        open={finderOpen}
        onClose={() => setFinderOpen(false)}
        onAllocated={setToast}
      />
      <Snackbar
        open={toast !== null}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        message={toast ?? ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        data-automation-id="planner-toast"
      />
    </Box>
  );
};
